import React, { useMemo, useState } from "react";
import { childrenOf, searchTypes, typePath } from "../../structure/buildingTypes";

export default function BuildingTypePicker({ index, value, onChange }) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rows = useMemo(() => (index ? searchTypes(index, query) : []), [index, query]);
  const picks = rows.filter((r) => !r.more);

  if (!index) return <div className="snote">კლასიფიკატორი იტვირთება…</div>;

  const pick = (code) => {
    onChange(code);
    setQuery("");
    setHighlight(0);
  };
  const path = typePath(index, value);
  const kids = path.length ? childrenOf(index, value) : [];
  const onKey = (e) => {
    if (e.key === "ArrowDown") setHighlight((h) => Math.min(h + 1, picks.length - 1));
    else if (e.key === "ArrowUp") setHighlight((h) => Math.max(h - 1, 0));
    else if (e.key === "Enter" && picks[highlight]) pick(picks[highlight].type.code);
    else if (e.key === "Escape") setQuery("");
    else return;
    e.preventDefault();
  };

  return (
    <>
      <div className="tsrch">
        <span aria-hidden="true">⌕</span>
        <input
          value={query}
          placeholder="აკრიფეთ: მაგ. სასტუმრო, school, ოფისი…"
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={onKey}
        />
        {query && (
          <button type="button" className="qx" onClick={() => setQuery("")} aria-label="გასუფთავება">
            ✕
          </button>
        )}
      </div>
      {query.trim() && (
        <div className="tlist">
          {!rows.length && <div className="tl-empty">„{query.trim()}“ — ვერაფერი მოიძებნა</div>}
          {rows.map((r) => {
            if (r.more)
              return (
                <button key={`more-${r.type.code}`} type="button" className="trow sub more" onClick={() => pick(r.type.code)}>
                  … კიდევ {r.more} ქვე-ტიპი — აირჩიეთ ჯგუფი
                </button>
              );
            const hl = picks[highlight] === r;
            const parentPath = typePath(index, r.type.code)
              .slice(0, -1)
              .map((x) => x.name_ka)
              .join(" › ");
            return (
              <button
                key={`${r.depth}-${r.type.code}`}
                type="button"
                className={`trow ${r.depth ? "sub" : r.kids ? "grp" : ""}${hl ? " hl" : ""}`}
                onClick={() => pick(r.type.code)}
              >
                {r.depth ? <span aria-hidden="true">└</span> : null}
                {r.kids && !r.depth ? <b>{r.type.name_ka}</b> : <span>{r.type.name_ka}</span>}
                {!r.depth && <span className="path">{parentPath}</span>}
                {r.kids > 0 && <span className="badge">{r.kids} ქვე-ტიპი</span>}
              </button>
            );
          })}
        </div>
      )}
      {path.length > 0 && (
        <div className="tsel">
          {path.map((x, i) => {
            const cur = i === path.length - 1;
            return (
              <button
                key={x.code}
                type="button"
                className={`lvl${cur ? " cur" : ""}`}
                style={{ paddingLeft: 12 + i * 14 }}
                onClick={() => !cur && pick(x.code)}
              >
                {i > 0 && <span aria-hidden="true">└</span>}
                <span>{x.name_ka}</span>
                {cur && <span className="code">{x.code}</span>}
                {cur && <span className="lvlmark">არჩეულია</span>}
              </button>
            );
          })}
        </div>
      )}
      {kids.length > 0 && <SubtypeList index={index} kids={kids} onPick={pick} />}
      {!path.length && !query.trim() && <SubtypeList index={index} kids={childrenOf(index, "")} onPick={pick} title="აირჩიეთ ტიპი" />}
      {path.length > 0 && !kids.length && <div className="snote">ბოლო დონეა — უფრო ზუსტი ტიპი არ არის.</div>}
    </>
  );
}

const FILTER_FROM = 8;

/** Sub-types of the chosen type: sorted, two columns, each with how many sub-types it has itself. */
function SubtypeList({ index, kids, onPick, title = "დაზუსტება" }) {
  const [filter, setFilter] = useState("");
  const sorted = useMemo(() => [...kids].sort((a, b) => a.name_ka.localeCompare(b.name_ka, "ka")), [kids]);
  const q = filter.trim().toLowerCase();
  const shown = q ? sorted.filter((k) => `${k.name_ka} ${k.name_en || ""}`.toLowerCase().includes(q)) : sorted;
  return (
    <div className="tsub">
      <div className="tsub-h">
        <span>
          {title} <small>· {kids.length} {title === "დაზუსტება" ? "ქვე-ტიპი" : "ტიპი"}</small>
        </span>
        {kids.length > FILTER_FROM && (
          <input className="inp tsub-f" value={filter} placeholder="ფილტრი…" onChange={(e) => setFilter(e.target.value)} />
        )}
      </div>
      <div className="tsub-grid">
        {shown.map((k) => {
          const deeper = childrenOf(index, k.code).length;
          return (
            <button key={k.code} type="button" className="tsub-i" onClick={() => onPick(k.code)} title={k.name_en || k.name_ka}>
              <span className="nm">{k.name_ka}</span>
              {deeper > 0 && <span className="cnt">{deeper} ›</span>}
            </button>
          );
        })}
        {!shown.length && <div className="tl-empty">„{filter}“ — ვერაფერი მოიძებნა</div>}
      </div>
    </div>
  );
}
